import React, { useEffect,useState } from 'react';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import generateId from '../../utils/generateId';
import makePath from '../../utils/makePath';
import { setItem } from '../../actions';
import { FormControl, FormHelperText, InputAdornment, InputLabel, makeStyles, OutlinedInput} from '@material-ui/core';
import { useDirty, useFormElementCleanUp, useValidation } from '../../hooks';
import { FormElementWrapper } from './FormElementWrapper';
import { ErrorHelpText } from './ErrorHelpText';

const useStyles = makeStyles( theme => ({
  formControl: {
    '& .react-datepicker-popper': {
      zIndex: 10,
    },
    '& .react-datepicker': {
      padding: 12,
      border: 'none',
      borderRadius: 4,
      boxShadow: '0 1px 8px rgba(0,0,0,.35)',
      fontFamily: theme.typography.fontFamilyInputs,
      fontSize: 12,
      fontWeight: 500,
    },
    '& .react-datepicker__header': {
      border: 'none',
      background: '#fff',
    },
    '& .react-datepicker__navigation': {
      height: 40,
      width: 40,
      border: 0,
      background: theme.palette.primary.light,
      color: theme.palette.primary.main,
      borderRadius: '50%',
      backgroundColor: '#fff',
      transition: 'background-color .3s, color .3s',
      '&::after': {
        fontFamily: "'Font Awesome 6 Free'",
        fontWeight: 700,
        position: 'absolute',
        left: 0,
        top: 0,
        display: 'block',
        textIndent: 0,
        lineHeight: '40px',
        height: '100%',
        width: '100%',
      },
      '&:hover': { 
        color: theme.palette.primary.hover,
        backgroundColor: theme.palette.primary.light,
      }
    },
    '& .react-datepicker__navigation--previous::after': {
      content: '"\\f053"',
    },
    '& .react-datepicker__navigation--next::after': {
      content: '"\\f054"',
    },
    '& .react-datepicker__current-month': {
      marginBottom: 10,
      fontSize: 18,
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    '& .react-datepicker__day-name': {
      color: theme.palette.neutral.dark1,
    },
    '& .react-datepicker__day-name, & .react-datepicker__day': {
      height: 34,
      width: 34,
      lineHeight: '35px',
      margin: 3,
    },
    '& .react-datepicker__day': {
      color: theme.palette.neutral.dark2,
      borderRadius: '50%',
      transition: 'background-color .3s, color .3s',
      '&--selected, &:hover, &--keyboard-selected': {
        borderRadius: '50%',
        color: '#fff',
        height: 40,
        width: 40,
        lineHeight: '40px',
        margin: 0,
      },
      '&--selected': {
        backgroundColor: theme.palette.primary.main,
      },
      '&--keyboard-selected': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.hover,
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.hover,       
      },
    }
  }
}))

export const DateInput = React.memo(({classes:propsClasses='',given,label,name, ...props}) => {
    
  // Validation conditions
  const validate = () => {
    if (!date) {
      setError('Missing! This field is required.');
    } else if (date.toDateString() === new Date().toDateString()) {
      setError('Missing! You need to replace the default text.');
    } else {
      setError('')
    }
  }

  
  const [date, setDate] = useState(new Date())
  const [id] = useState(generateId('dateInput-'));

  const dispatch = useDispatch();
  const setDirty = useDirty();  
  
  const path = makePath(props.path, name);
  useFormElementCleanUp(path);

  // Apply validation conditions
  const [error, setError] = useValidation(validate,date,path,id);


  const classes = useStyles();

  // Set state from given value, if provided
  useEffect(() => {
    if (given) {
      setDate(new Date(given));
    }
  }, [given]);


  // Update store when state changes
  useEffect(() => {
    dispatch(setItem(path, date));
  }, [date,dispatch,path]);

  
  // Update state when date is picked from datepicker
  const onDateChange = (value) => {
    const date = new Date(value);
    setDirty();
    setDate(date)
    if (error) {
      setError('')
    }
  }



  return (
    <FormElementWrapper className={propsClasses}>
      <FormControl className={classes.formControl}>
        <InputLabel htmlFor={id} error={!!error}>{label}</InputLabel>
          
        <DatePicker
          id={id}
          showPopperArrow={false}
          popperPlacement="bottom-start" // wishful thinking... :( doesn't work
          popperModifiers={{
            preventOverflow: {
              enabled: true,
              escapeWithReference: false,
              boundariesElement: 'scrollParent'
            }
          }}        
          selected={date}
          onChange={onDateChange}
          customInput={
            <OutlinedInput 
              variant="outlined"
              fullWidth
              error={!!error}
              aria-describedby={`${id}-helper-text ${id}-error`}
              endAdornment={
                <InputAdornment position="end">
                  <i className="far fa-calendar-alt"></i>
                </InputAdornment>
              }
            />
          }
        />

        <ErrorHelpText error={error} id={id} />

        <FormHelperText id={`${id}-helper-text`} variant="filled">
          Use format MM/DD/YYYY.
        </FormHelperText>

       
      </FormControl>
  </FormElementWrapper>
    
  )

})


DateInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  classes: PropTypes.string,
  path: PropTypes.string
}